import {
  Button,
  Card,
  Flex,
  Form,
  Input,
  InputNumber,
  Table,
  theme,
  Tooltip,
  Typography,
} from 'antd';
import { RotateCcw, Search } from 'lucide-react';
import { type ReactNode, useEffect, useState } from 'react';
import { catchError, EMPTY, tap } from 'rxjs';
import LoadingSpin from '../../../../components/loading-spin';
import SyncSuspense from '../../../../components/sync-suspense';
import { type CycleMenuResponseDto } from '../../../../dto/cycle-menu/cycle-menu-response-dto';
import { Beverage, type CycleMenu, CycleMenuBeverage } from '../../../../entities';
import { useNotification } from '../../../../hooks/use-notification';
import { AtomicOperation, useAtomicOperations, useQuery } from '../../../../jsonapi-provider';
import utils from '../../../../utils/utils';

interface PropsType {
  cycleMenu: CycleMenuResponseDto;
}

const AssignedBeveragesPage = (props: PropsType): ReactNode => {
  const { token } = theme.useToken();
  const notification = useNotification();
  const atomicOperations = useAtomicOperations();
  const [formInstance] = Form.useForm();
  const { cycleMenu } = props;
  const [searchText, setSearchText] = useState('');

  const beverages = useQuery<Beverage[]>(['beverage', { page: { limit: 10000 } }]);
  const cycleMenuQuery = useQuery<CycleMenu>(
    cycleMenu?.id
      ? [
          'cycle-menu',
          String(cycleMenu.id),
          {
            include: ['cycleMenuBeverages.beverage'],
            fields: {
              'cycle-menu': ['cycleMenuBeverages'],
              'cycle-menu-beverage': ['beverage', 'price'],
              beverage: ['name', 'price'],
            },
          },
        ]
      : null
  );

  const beveragesData =
    Form.useWatch(['beverages'], { form: formInstance, preserve: true }) ??
    formInstance.getFieldValue('beverages') ??
    [];

  useEffect(() => {
    if (!cycleMenu?.id || !beverages.data) return;
    const preparedValues: any = beverages.data?.map((b) => ({
      price: b.price,
      name: b.name,
      id: b.id,
      defaultPrice: b.price,
      canReset: false,
    }));
    const assignedBeverages = cycleMenuQuery.data?.cycleMenuBeverages ?? [];
    assignedBeverages.forEach((bcp) => {
      const beveragePrice = preparedValues.find((b: any) => b.id === bcp.beverage.id);
      if (beveragePrice) {
        beveragePrice.price = bcp.price;
        beveragePrice.childPrice = bcp.price;
        beveragePrice.canReset = true;
        beveragePrice.cmbId = bcp.id;
      }
    });
    formInstance.setFieldValue('beverages', preparedValues);
  }, [cycleMenu.id, beverages.data, cycleMenuQuery.data]);

  const getBeveragesOperations = (beveragesToBeSubmitted: any): any => {
    return beveragesToBeSubmitted
      .map((btbs: any) => {
        if (
          btbs.cmbId !== undefined &&
          ((btbs.parentPrice !== undefined && btbs.parentPrice === btbs.price) ||
            (btbs.parentPrice === undefined && btbs.price === btbs.defaultPrice))
        ) {
          return AtomicOperation.remove([
            'cycle-menu',
            String(cycleMenu.id),
            'cycleMenuBeverages',
            btbs.cmbId,
          ]);
        } else if (
          btbs.cmbId !== undefined &&
          ((btbs.parentPrice !== undefined && btbs.parentPrice !== btbs.price) ||
            (btbs.parentPrice === undefined && btbs.price !== btbs.defaultPrice))
        ) {
          return AtomicOperation.update(
            new CycleMenuBeverage(btbs.cmbId!, btbs.price, new Beverage(btbs.id)),
            undefined,
            ['cycle-menu', String(cycleMenu.id), 'cycleMenuBeverages', btbs.cmbId]
          );
        } else if (
          btbs.cmbId === undefined &&
          ((btbs.parentPrice !== undefined && btbs.parentPrice !== btbs.price) ||
            (btbs.parentPrice === undefined && btbs.price !== btbs.defaultPrice))
        ) {
          return AtomicOperation.add(
            Object.assign(new CycleMenuBeverage(undefined, btbs.price, new Beverage(btbs.id)), {
              cycleMenu: undefined,
            }),
            undefined,
            ['cycle-menu', String(cycleMenu.id), 'cycleMenuBeverages']
          );
        } else {
          return undefined;
        }
      })
      .filter((el: any) => el !== undefined);
  };

  const onSave = (): void => {
    const beveragesOperations = getBeveragesOperations(beveragesData);
    if (beveragesOperations.length === 0) {
      notification.info({ message: '', description: 'No changes detected to save' });
      return;
    }
    atomicOperations
      .operate(beveragesOperations)
      .pipe(
        tap(() => {
          cycleMenuQuery.refetch?.();
          notification.success({
            message: '',
            description: 'Beverages updated successfully',
          });
        }),
        catchError((error: any) => {
          notification.error({
            message: error?.title ?? '',
            description:
              error?.detail ??
              error?.message ??
              (typeof error === 'string' ? error : 'Failed to update beverages'),
          });
          return EMPTY;
        })
      )
      .subscribe();
  };

  const onDiscard = (): void => {
    cycleMenuQuery.refetch?.();
    notification.info({ message: '', description: 'Changes discarded' });
  };

  const columns = [
    {
      key: 'action',
      width: 30,
      render: (_: any, record: any) =>
        record.canReset ? (
          <Tooltip
            title={`Reset to ${`the default price ${utils.currencyFormat(record.defaultPrice)}`}`}
          >
            <Button
              shape="circle"
              size="small"
              type="text"
              style={{ color: token.colorInfo }}
              icon={<RotateCcw />}
              onClick={() => {
                formInstance.setFieldValue(
                  [
                    'beverages',
                    formInstance
                      .getFieldValue('beverages')
                      .findIndex((el: any) => el.id === record.id),
                  ],
                  { ...record, price: record.defaultPrice, canReset: false }
                );
              }}
            />
          </Tooltip>
        ) : (
          <></>
        ),
    },
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      width: '65%',
      filterSearch: true,
      onFilter: (value: any, record: any) => record.name.startsWith(value as string),
    },
    {
      title: 'Price',
      dataIndex: 'price',
      key: 'price',
      width: '30%',
      render: (_: any, record: any) => {
        const itemIdx = beveragesData.findIndex((el: any) => el.id === record.id);
        const currentPrice = itemIdx >= 0 ? beveragesData[itemIdx]?.price : record.price;
        return (
          <InputNumber
            precision={2}
            style={{ width: '100%', maxWidth: 100 }}
            min={0}
            value={currentPrice}
            formatter={(v) => utils.currencyFormat(Number(v) ?? 0)}
            onChange={(val) => {
              if (itemIdx >= 0) {
                formInstance.setFieldValue(['beverages', itemIdx], {
                  ...record,
                  price: val,
                  canReset: val !== record.defaultPrice,
                });
              }
            }}
          />
        );
      },
    },
  ];

  const filteredData = beveragesData.filter(
    (item: Beverage) => item.name?.toLowerCase().includes(searchText.toLowerCase())
  );

  return (
    <Form form={formInstance} component={false}>
      <Flex gap="middle" vertical>
        <Flex gap="middle" justify="space-between" align="center">
          <Flex gap="small">
            <Typography.Title level={3}>Beverages</Typography.Title>
          </Flex>
        </Flex>
        <SyncSuspense
          fallback={
            <Card style={{ marginBlockEnd: token.paddingXL * 6 }}>
              <LoadingSpin />
            </Card>
          }
          condition={!beverages.isLoading && !cycleMenuQuery.isLoading}
        >
          <Input
            placeholder="Search beverages..."
            prefix={<Search size={16} />}
            style={{ width: 300 }}
            value={searchText}
            onChange={(e) => {
              setSearchText(e.target.value);
            }}
            allowClear
          />

          <Table
            columns={columns}
            dataSource={filteredData}
            rowKey={(record) => record.id}
            pagination={{ pageSize: 10 }}
            size="middle"
            style={{ marginBlockEnd: token.paddingXL * 6 }}
          />
        </SyncSuspense>

        <Flex
          className="floating"
          gap="small"
          style={{
            position: 'absolute',
            bottom: 0,
            flexDirection: 'row-reverse',
            width: `calc(100% - ${token.paddingMD * 2}px)`,
            paddingBlockStart: token.paddingXL * 2.5,
            paddingBlockEnd: token.paddingXL * 2,
            paddingInline: token.paddingXL,
            pointerEvents: 'none',
          }}
        >
          <Button
            size="large"
            type="primary"
            onClick={onSave}
            loading={atomicOperations.isLoading}
            disabled={atomicOperations.isLoading}
            style={{ pointerEvents: 'auto' }}
          >
            Save
          </Button>
          <Button
            size="large"
            onClick={onDiscard}
            disabled={atomicOperations.isLoading}
            style={{ pointerEvents: 'auto' }}
          >
            Discard Changes
          </Button>
        </Flex>
      </Flex>
    </Form>
  );
};

export default AssignedBeveragesPage;
